import { useState, useEffect, useCallback } from 'react'
import { Box, VStack, Text } from '@chakra-ui/react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { BrandBar } from '../../components/BrandBar'
import { TitleHeader } from '../../components/TitleHeader'
import { PurchaseSummary } from '../components/PurchaseSummary'
import { PaymentMethodSelector } from '../components/PaymentMethodSelector'
import { PurchaseButton } from '../components/PurchaseButton'
import { BackToCoinPurchaseLink } from '../components/BackToCoinPurchaseLink'
import { FooterLinks } from '../components/FooterLinks'
import { useCreditCardSetup } from '../hooks/useCreditCardSetup'
import { usePayPayPurchase } from '../hooks/usePayPayPurchase'
import { useErrorToast } from '../hooks/useErrorToast'
import { submitPaymentForm } from '../services/paymentFormSubmitter'
import { getSelectPaymentMethodErrorMessage } from '../services/selectPaymentMethodErrors'
import { useLogout } from '../../hooks/useLogout'

export const SelectPaymentMethod = ({ coinPlans }) => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { logout, isLoading: isLogoutLoading } = useLogout()

  const productId = searchParams.get('product_id')
  const [selectedMethod, setSelectedMethod] = useState('credit_card')

  // product_idからコインプランを検索
  const coinPlan = coinPlans?.find((plan) => plan.product_id === productId)

  // カスタムフック使用
  const { setup: setupCreditCard, isLoading: isCreditCardSetupLoading } = useCreditCardSetup()
  const { payWithPayPay, isLoading: isPayPayLoading } = usePayPayPurchase()
  const { showError } = useErrorToast()

  // product_idが不正な場合はリダイレクト
  useEffect(() => {
    // coinPlansが読み込まれた後にのみチェック
    if (coinPlans && coinPlans.length > 0 && (!productId || !coinPlan)) {
      showError({
        title: 'コインが選択されていません',
        errorMessage: '',
        duration: 3000,
      })
      navigate('/')
    }
  }, [productId, coinPlan, coinPlans, navigate, showError])

  // クレジットカード決済処理
  const handleCreditCardPurchase = useCallback(async () => {
    let result
    try {
      result = await setupCreditCard()
    } catch (err) {
      const errorIdentifier = err.data?.error || 'unknown_error'
      showError({
        title: 'エラー',
        errorMessage: getSelectPaymentMethodErrorMessage(errorIdentifier),
      })
      return
    }

    if (!result.hasCard) {
      // カード未登録の場合は新規カード登録画面へ
      navigate(`/new_card?product_id=${productId}`, {
        state: { isUpdate: false },
      })
      return
    }

    // カード情報をstateで渡して確認画面へ遷移
    navigate(`/confirm?product_id=${productId}`, {
      state: {
        cardInfo: result.cardInfo,
        isUpdate: true,
      },
    })
  }, [setupCreditCard, showError, navigate, productId])

  // PayPay決済処理
  const handlePayPayPurchase = useCallback(async () => {
    let result
    try {
      result = await payWithPayPay(productId)
    } catch (err) {
      const errorIdentifier = err.data?.error || 'unknown_error'
      showError({
        title: 'エラー',
        errorMessage: getSelectPaymentMethodErrorMessage(errorIdentifier),
      })
      return
    }

    const { access_id, token, start_url } = result
    submitPaymentForm(start_url, access_id, token)
  }, [payWithPayPay, productId, showError])

  // 購入処理
  const handlePurchase = useCallback(() => {
    if (selectedMethod === 'credit_card') {
      handleCreditCardPurchase()
    } else if (selectedMethod === 'paypay') {
      handlePayPayPurchase()
    }
  }, [selectedMethod, handleCreditCardPurchase, handlePayPayPurchase])

  // データ読み込み中またはコインプランが見つからない場合は何も表示しない
  if (!coinPlan) {
    return null
  }

  return (
    <Box minH="100vh" bg="background.default" display="flex" justifyContent="center">
      <Box w="100%" maxW="500px">
        <VStack gap={0} align="stretch">
          <BrandBar onLogout={logout} isLogoutLoading={isLogoutLoading} onLogoClick={() => navigate('/')} />
          <TitleHeader title="購入方法選択" />

          <Box py="20px" px="16px">
            <VStack gap={0} align="stretch">
              <PurchaseSummary coinCount={coinPlan.coin} price={coinPlan.web_price} />

              <Text fontSize="md" fontWeight="600" color="label.primary" lineHeight="22px" mt="24px" mb="16px">
                購入方法を選択してください
              </Text>

              <PaymentMethodSelector selectedMethod={selectedMethod} onMethodChange={setSelectedMethod} />

              <PurchaseButton onClick={handlePurchase} isLoading={isPayPayLoading || isCreditCardSetupLoading} mt="24px" />

              <BackToCoinPurchaseLink onClick={() => navigate('/')} />
            </VStack>
          </Box>

          <FooterLinks />
        </VStack>
      </Box>
    </Box>
  )
}
